import { motion } from 'framer-motion';
import { FaUpload, FaFilePdf, FaImage } from 'react-icons/fa';
import { useState, useRef } from 'react';
import { usePDFUpload } from '../../hooks/usePDFUpload';

const UploadButton = ({ sampleId, onUpload }) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);
  const { uploadPDF, isUploading, error } = usePDFUpload();

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (file) {
      await processFile(file);
    }
  };

  const handleThumbnailSelect = async (event) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        // Get the PDF from main input
        const pdfFile = fileInputRef.current?.files?.[0];
        if (pdfFile) {
          onUpload(sampleId, pdfFile, e.target.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const processFile = async (file) => {
    if (file.type !== 'application/pdf') {
      alert('Please upload a PDF file');
      return;
    }

    // For demo: just upload without thumbnail
    // In production, you might want to generate thumbnail or require one
    const result = await uploadPDF(file);
    if (result.success) {
      onUpload(sampleId, file, null);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);

    const file = event.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  return (
    <div className="space-y-3">
      {/* PDF Upload */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-all ${
          isDragging
            ? 'border-primary-green bg-primary-green/10'
            : 'border-primary-green/30 hover:border-primary-green bg-white'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          onChange={handleFileSelect}
          className="hidden"
          id={`pdf-upload-${sampleId}`}
        />

        <label
          htmlFor={`pdf-upload-${sampleId}`}
          className="cursor-pointer"
        >
          <FaFilePdf className="text-5xl text-primary-green mx-auto mb-3" />
          <h4 className="font-display font-bold text-primary-green-dark mb-2">
            Upload PDF Sample
          </h4>
          <p className="text-sm text-neutral-gray-dark mb-3">
            Drag & drop or click to browse
          </p>
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center gap-2 bg-primary-green text-white px-6 py-2 rounded-button font-display font-semibold"
          >
            <FaUpload />
            <span>Choose PDF File</span>
          </motion.div>
        </label>
      </div>

      {/* Optional Thumbnail Upload */}
      <div className="border-2 border-dashed border-accent-orange/30 hover:border-accent-orange rounded-lg p-4 text-center bg-white transition-all">
        <input
          ref={thumbnailInputRef}
          type="file"
          accept="image/*"
          onChange={handleThumbnailSelect}
          className="hidden"
          id={`thumbnail-upload-${sampleId}`}
        />

        <label
          htmlFor={`thumbnail-upload-${sampleId}`}
          className="cursor-pointer"
        >
          <FaImage className="text-3xl text-accent-orange mx-auto mb-2" />
          <h5 className="font-display font-semibold text-sm text-primary-green-dark mb-1">
            Upload Thumbnail (Optional)
          </h5>
          <p className="text-xs text-neutral-gray-dark mb-2">
            Custom preview image
          </p>
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center gap-2 bg-accent-orange text-white px-4 py-1 rounded-full font-display font-semibold text-sm"
          >
            <FaImage />
            <span>Add Image</span>
          </motion.div>
        </label>
      </div>

      {/* Upload Status */}
      {isUploading && (
        <div className="text-center py-2">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary-green"></div>
          <p className="text-sm text-primary-green mt-2">Uploading...</p>
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded">
          {error}
        </div>
      )}

      {/* Info Text */}
      <p className="text-xs text-neutral-gray-dark text-center">
        💡 PDF files only • Max 10MB • For demo purposes
      </p>
    </div>
  );
};

export default UploadButton;